import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Moon, Activity, Brain, Flame, Apple, Scale, 
  Users, FileText, Sparkles, Shield, PawPrint, 
  MessageCircle, MapPin, Heart, Plus, X 
} from 'lucide-react';

const features = [
  {
    icon: Moon,
    title: 'Sleep & Rest',
    subtitle: 'Wake up knowing your body',
    details: ['Sleep stages (light/deep/REM)', 'Consistency score', 'Disturbances & quality insights', 'Weekly trends', 'Sleep improvement suggestions'],
  },
  {
    icon: Activity,
    title: 'Recovery',
    subtitle: 'Know when to push, when to rest',
    details: ['Recovery score (sleep + HRV + resting HR)', 'Readiness guidance', 'Recovery trends', 'Personalized tips', 'Family overview at a glance'],
  },
  {
    icon: Brain,
    title: 'Stress',
    subtitle: 'See what your mind feels',
    details: ['Stress timeline', 'Calm vs elevated moments', 'Trigger patterns', 'Reset suggestions', 'Privacy controls'],
  },
  {
    icon: Flame,
    title: 'Strain',
    subtitle: 'Every effort, measured',
    details: ['Daily strain score', 'Intensity insights', 'Overtraining warnings', 'Weekly progress', 'Family encouragement'],
  },
  {
    icon: Apple,
    title: 'Nutrition Tracking',
    subtitle: 'Fuel your potential',
    details: ['Food logging', 'Macro estimates', 'Meal timing insights', 'Goal-based suggestions', 'Family-friendly routines'],
  },
  {
    icon: Scale,
    title: 'Calories Tracking',
    subtitle: 'Balance in, balance out',
    details: ['Estimated burn', 'Optional intake tracking', 'Maintain/lose/gain goals', 'Weekly balance view', 'Simple non-judgmental UX'],
  },
  {
    icon: Users,
    title: 'Connect Families',
    subtitle: 'Health is better together',
    details: ['Private family dashboard', 'Shared summaries', 'Goals & streaks', 'Gentle nudges', 'Built for kids to grandparents'],
  },
  {
    icon: FileText,
    title: 'Daily Reports',
    subtitle: 'Your day, summarized',
    details: ['Daily PDF report', 'Key metrics summary', 'Highlights + trends', 'Weekly recap', 'Share inside family group'],
  },
  {
    icon: Sparkles,
    title: 'Connect Intelligence',
    subtitle: 'AI that knows you',
    details: ['Personalized recommendations', 'Diet + routine suggestions', 'Predictive insights', 'Small wins coaching', 'Family-friendly guidance'],
  },
  {
    icon: Shield,
    title: 'Care & Safety',
    subtitle: 'Peace of mind, always',
    details: ['GPS safety layer', 'Safe zones + entry/exit alerts', 'Emergency SOS', 'Check-ins', 'Designed for care use cases'],
  },
  {
    icon: PawPrint,
    title: 'Pet Health & Location',
    subtitle: "Because they're family too",
    details: ['Pet GPS + safe zones', 'Activity/rest patterns', 'Away-from-home alerts', 'Family can view pet status', 'Works in the Connect app ecosystem'],
  },
  {
    icon: MessageCircle,
    title: 'Family Group Chat',
    subtitle: 'Stay close, effortlessly',
    details: ['Private family chat in the Connect app', 'Quick check-ins and updates', 'Share daily reports and wellness summaries', 'Alerts can post into the family chat', 'Works for Duo, Family, and Care groups'],
  },
  {
    icon: MapPin,
    title: 'Kids Care & Location',
    subtitle: "See your child's whereabouts",
    details: ['Real-time child location', 'Activity tracking for kids', 'Safe-zone alerts—without constant calls', 'School arrival/departure notifications', 'Kid-friendly privacy settings'],
  },
  {
    icon: Heart,
    title: 'Parents 60+ Health & Sleep',
    subtitle: 'Caring for those who cared for you',
    details: ['Heart rate pattern monitoring', 'Sleep quality tracking', 'Gentle alerts when trends change', 'Caregiver notifications', 'Respectful, non-intrusive design'],
  },
];

const FeatureCard = ({ feature, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.03 }}
      className="relative"
      data-testid={`feature-card-${index}`}
    >
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={`feature-tile ${isExpanded ? 'expanded' : ''}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-black/5 flex items-center justify-center flex-shrink-0">
              <feature.icon className="w-4 h-4 text-[#4A4A4A]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1A1A1A] mb-0.5">{feature.title}</h3>
              <p className="text-xs text-[#6A6A6A]">{feature.subtitle}</p>
            </div>
          </div>
          <button
            className={`w-7 h-7 rounded-full bg-black/5 flex items-center justify-center 
                       flex-shrink-0 transition-all duration-300 hover:bg-black/10
                       ${isExpanded ? 'rotate-45' : ''}`}
          >
            {isExpanded ? <X className="w-3.5 h-3.5 text-[#6A6A6A]" /> : <Plus className="w-3.5 h-3.5 text-[#6A6A6A]" />}
          </button>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <ul className="mt-3 pt-3 border-t border-black/5 space-y-1.5">
                {feature.details.map((detail, i) => (
                  <li key={i} className="text-xs text-[#5A5A5A] flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const FeaturesGrid = () => {
  return (
    <section id="features" className="section" data-testid="features-section">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] mb-3">
            Everything You Need to Thrive.
          </h2>
          <p className="text-[#5A5A5A] text-base md:text-lg">
            Tap any feature to learn more.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[#7A7A7A] text-sm mt-8"
        >
          All features work across One, Duo, Family, and Care — with privacy controls.
        </motion.p>
      </div>
    </section>
  );
};

export default FeaturesGrid;
